import { gql } from '@apollo/client';
import axios from 'axios';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';
import { StatusTorneio } from '../utils/StatusTorneio';
import { DescontoCategoria } from './descontoTorneioService';
import { buscarEquipesPorCategoriaTorneio } from './equipeService';
import type { InscritoAtleta } from '../types/inscricao';
import { toLocalDateTimeString } from '../utils/date';

const restUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT + 'api/';

export interface Organizador {
  id: string;
  nome: string;
  telefone?: string | null;
  fotoUrl?: string | null;
  nomeFuncao?: string | null;
  usuario?: {
    id: string;
    nome: string;
    username: string;
    imagemUrl?: string | null;
  } | null;
}

export const LISTAR_TORNEIOS = gql`
  query ListarTorneios {
    torneios {
      id
      nome
      subtitulo
      dataInicio
      dataFim
      avatarUrl
      bannerUrl
      bannerVUrl
      regulamento
      categoriasTorneio {
        id
        nome
        descricao
        aplicarDescontoTorneio
      }
      local {
        id
        nome
        urlLocalizacao
        endereco {
          id
          cidade {
            id
            nome
            uf
          }
        }
      }
    }
  }
`;

export interface Torneio {
  id: string;
  nome: string;
  descricao?: string;
  subtitulo?: string;
  username?: string;
  dataInicio: string;
  dataFim?: string;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  bannerVUrl?: string | null;
  regulamento?: string | null;
  seguidorId?: string | null;
  dataInscricaoInicio?: string | null;
  dataInscricaoFim?: string | null;
  categoriasTorneio?: CategoriaTorneio[];
  ativo?: boolean;
  status?: StatusTorneio | null;
  filaEspera?: boolean | null;
  autoFilaEspera?: boolean | null;
  exibirFilaEspera?: boolean | null;
  camisetas?: boolean | null;
  sapatos?: boolean | null;
  atletaTrocaParceiro?: boolean | null;
  exibirInscritos?: boolean | null;
  autoCancNaoPagas?: boolean | null;
  validarElegibilidade?: boolean | null;
  criadoEm?: string;
  atualizadoEm?: string;
  totalCategorias?: number | null;
  categoriasAtivas?: number | null;
  totalInscricoes?: number | null;
  inscricoesNaoPagas?: number | null;
  totalPartidas?: number | null;
  partidasSemResultado?: number | null;
  local: {
    id: string;
    nome: string;
    descricao?: string;
    urlLocalizacao?: string | null;
    endereco?: {
      id: string;
      cidade: {
        id: string;
        nome: string;
        uf: string;
      };
      logradouro?: string;
      numero?: string;
      complemento?: string;
      bairro?: string;
      cep?: string;
    };
  };
  patrocinadores: {
    id: string;
    nome: string;
    fotoUrl?: string | null;
    descricao?: string | null;
    siteUrl?: string | null;
  }[];
  organizadores?: Organizador[];
  descontoCategorias?: (DescontoCategoria & { descricao?: string | null })[];
  descontosCategoria?: (DescontoCategoria & { descricao?: string | null })[];
  liga?: { id: string; nome: string; imagemUrl?: string | null; username?: string | null } | null;
  ligaId?: string | null;
}

export interface TorneioProfile {
  id: string;
  nome: string;
  descricao?: string;
  subtitulo?: string;
  username?: string;
  dataInicio: string;
  dataFim?: string;
  dataInscricaoInicio?: string | null;
  dataInscricaoFim?: string | null;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  bannerVUrl?: string | null;
  seguidorId?: string | null;
  organizadores?: Organizador[];
}

export interface CategoriaTorneio {
  id: string;
  nome: string;
  descricao?: string;
  genero?: string;
  precoInscricao?: number;
  regulamento?: string | null;
  numeroAtletas?: number;
  aplicarDescontoTorneio?: boolean | null;
  configFilaEspera?: boolean | null;
  inscricoesAtivasCount?: number | null;
  status?: string | null;
  dataFim?: string | null;
  dataInicio?: string | null;
  dataInicioInscricao?: string | null;
  dataFimInscricao?: string | null;
  dataLimPag?: string | null;
  vagas?: number;
  ocupacao?: {
    inscritos: number;
    vagas?: number | null;
    filaEspera: number;
    vagasDisponiveis?: number | null;
  } | null;
  modeloDisputa?: string | null;
  formatoPartidas?: string | null;
  idadeInicial?: number | null;
  idadeFinal?: number | null;
  idadeCombinada?: number | null;
  quantidadeGrupos?: number | null;
  classificadosPorGrupo?: number | null;
  tipoPontuacao?: string | null;
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  quantidadeFases?: number;
  fasesCategoria?: {
    id: string;
    partidas?: {
      id: string;
      status?: string | null;
      placar?: {
        id: string;
        resultadoFinalA?: number | null;
        resultadoFinalB?: number | null;
      } | null;
    }[];
  }[];
  partidas?: {
    id: string;
  }[];
}

export interface EstatisticasTorneioData {
  torneioPorId: {
    id: string;
    categoriasTorneio: {
      id: string;
      status?: string | null;
      dataFim?: string | null;
      partidas: { id: string; placar?: { id: string } | null }[];
    }[];
  };
  inscricoesTorneioPorAtletaIdTorneioId: { id: string; status: string }[];
}

export interface EstatisticasTorneioVars {
  torneioId: string;
}

export interface ListarTorneiosData {
  torneios: Torneio[];
}

export const TORNEIO_POR_ID = gql`
  query TorneioPorId($id: ID!) {
    torneioPorId(id: $id) {
      ativo
      atualizadoEm
      avatarUrl
      bannerUrl
      bannerVUrl
      categoriasTorneio {
        descricao
        genero
        id
        nome
        categoriaLiga {
          id
          nome
          combinaveis {
            id
          }
        }
        precoInscricao
        regulamento
        numeroAtletas
        aplicarDescontoTorneio
        configFilaEspera
        inscricoesAtivasCount
        vagas
        ocupacao {
          inscritos
          vagas
          filaEspera
          vagasDisponiveis
        }
        status
        dataFim
        dataInicio
        dataLimPag
        modeloDisputa
        idadeInicial
        idadeFinal
        idadeCombinada
        quantidadeGrupos
        classificadosPorGrupo
        tipoPontuacao
        numeroSets
        gamesPorSets
        tiebreaks
        quantidadeFases
        fasesCategoria {
          id
          partidas {
            id
            status
            placar { 
              id
              resultadoFinalA
              resultadoFinalB
            }
          }
        }
        partidas {
          id
        }
      }
      criadoEm
      dataFim
      dataInicio
      dataInscricaoInicio
      dataInscricaoFim
      descricao
      id
      local {
        descricao
        id
        nome
        urlLocalizacao
        endereco {
          bairro
          cep
          complemento
          id
          logradouro
          numero
          principal
          cidade { id nome uf }
        }
      }
      nome
      username
      regulamento
      subtitulo
      seguidorId
      status
      ligaId
      liga {
        id
        nome
        imagemUrl
        username
      }
      filaEspera
      autoFilaEspera
      exibirFilaEspera
      camisetas
      sapatos
      atletaTrocaParceiro
      exibirInscritos
      autoCancNaoPagas
      validarElegibilidade
      totalInscricoes
      patrocinadores {
        id
        nome
        descricao
        fotoUrl
      }
      organizadores {
        id
        nome
        telefone
        fotoUrl
        nomeFuncao
        usuario {
          id
          nome
          username
          imagemUrl
        }
      }
      descontoCategorias {
        id
        tipo
        valor
        ordem
      }
      descontosCategoria: descontoCategorias {
        id
        tipo
        valor
        ordem
      }
    }
  }
`;

export interface TorneioPorIdData {
  torneioPorId: Torneio;
}

export interface TorneioPorIdVars {
  id: string;
}

export const TORNEIOS_FILTRADOS = gql`
  query TorneiosFiltrados(
    $nome: String
    $cidade: String
    $ligaId: ID
    $data: String
    $status: StatusTorneio
    $ativo: Boolean
    $page: Int!
    $size: Int!
    $sortBy: String
    $direction: String
    $ocultarPassados: Boolean
    $apenasVigentes: Boolean
  ) {
    torneiosFiltrados(
      nome: $nome
      cidade: $cidade
      ligaId: $ligaId
      data: $data
      status: $status
      ativo: $ativo
      page: $page
      size: $size
      sortBy: $sortBy
      direction: $direction
      ocultarPassados: $ocultarPassados
      apenasVigentes: $apenasVigentes
    ) {
      page
      size
      totalItems
      totalPages
      items {
        ativo
        atualizadoEm
        avatarUrl
        bannerUrl
        bannerVUrl
        criadoEm
        dataFim
        dataInicio
        dataInscricaoInicio
        dataInscricaoFim
        descricao
        id
        nome
        regulamento
        seguidorId
        status
        subtitulo
        username
        totalCategorias
        categoriasAtivas
        totalInscricoes
        inscricoesNaoPagas
        totalPartidas
        partidasSemResultado
        local {
          descricao
          nome
          id
          urlLocalizacao
          endereco {
            id
            logradouro
            numero
            principal
            complemento
            cep
            bairro
            cidade {
              id
              nome
              uf
            }
          }
        }
        organizadores {
          id
          nome
          usuario {
            id
          }
        }
        categoriasTorneio {
          descricao
          genero
          id
          nome
          numeroAtletas
          precoInscricao
          regulamento
          aplicarDescontoTorneio
          configFilaEspera
          inscricoesAtivasCount
          status
          dataFim
          dataInicio
          dataLimPag
          modeloDisputa
          idadeInicial
          idadeFinal
          idadeCombinada
          quantidadeGrupos
          classificadosPorGrupo
          tipoPontuacao
          numeroSets
          gamesPorSets
          tiebreaks
          vagas
        }
      }
    }
  }
`;

export const ESTATISTICAS_TORNEIO = gql`
  query EstatisticasTorneio($torneioId: ID!) {
    torneioPorId(id: $torneioId) {
      id
      categoriasTorneio {
        id
        status
        dataFim
        partidas {
          id
          placar { id }
        }
      }
    }
    inscricoesTorneioPorAtletaIdTorneioId(atletaId: "", torneioId: $torneioId) {
      id
      status
    }
  }
`;

export async function buscarTorneiosPorLiga(ligaId: string): Promise<Torneio[]> {
  try {
    const { data } = await apolloClient.query<TorneiosFiltradosData, TorneiosFiltradosVars>({
      query: TORNEIOS_FILTRADOS,
      variables: { ligaId, page: 0, size: 1000 },
      fetchPolicy: 'no-cache',
    });
    return data.torneiosFiltrados.items;
  } catch (error: any) {
    let message = 'Erro ao buscar torneios da liga.';
    if (error?.graphQLErrors?.[0]?.message) {
      message = error.graphQLErrors[0].message;
    } else if (error?.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
}

/**
 * Busca um torneio por id (inclui `liga { id }`). Usado para resolver o ligaId do
 * torneio no fluxo de CRIAÇÃO de categoria pela página admin, onde ainda não há
 * `CategoriaTorneio` carregada com `torneio.liga`. Propaga o erro — o chamador
 * decide a degradação (esconder o seletor de template é opcional).
 */
export async function buscarTorneioPorId(id: string): Promise<Torneio | null> {
  const { data } = await apolloClient.query<TorneioPorIdData, TorneioPorIdVars>({
    query: TORNEIO_POR_ID,
    variables: { id },
    fetchPolicy: 'cache-first',
  });
  return (data?.torneioPorId as Torneio) ?? null;
}

export async function buscarEstatisticasTorneio(torneioId: string) {
  try {
    const { data } = await apolloClient.query<EstatisticasTorneioData, EstatisticasTorneioVars>({
      query: ESTATISTICAS_TORNEIO,
      variables: { torneioId },
      fetchPolicy: 'no-cache',
    });
    return data;
  } catch {
    return null;
  }
}

export const ATIVAR_TORNEIO = gql`
  mutation AtivarTorneio($id: ID!) {
    ativarTorneio(id: $id) {
      ativo
      atualizadoEm
      avatarUrl
      bannerUrl
      bannerVUrl
      criadoEm
      dataFim
      dataInicio
      dataInscricaoFim
      dataInscricaoInicio
      descricao
      id
      nome
      regulamento
      seguidorId
      status
      subtitulo
      username
    }
  }
`;

export const INATIVAR_TORNEIO = gql`
  mutation InativarTorneio($id: ID!) {
    inativarTorneio(id: $id) {
      ativo
      atualizadoEm
      avatarUrl
      bannerUrl
      bannerVUrl
      criadoEm
      dataFim
      dataInscricaoFim
      dataInicio
      dataInscricaoInicio
      descricao
      id
      nome
      regulamento
      seguidorId
      status
      subtitulo
      username
    }
  }
`;

export interface InativarTorneioVars {
  id: string;
}

export interface InativarTorneioData {
  inativarTorneio: Torneio & { status?: string | null };
}

export async function inativarTorneio(id: string): Promise<Torneio & { status?: string | null }> {
  try {
    const { data } = await apolloClient.mutate<InativarTorneioData, InativarTorneioVars>({
      mutation: INATIVAR_TORNEIO,
      variables: { id },
    });
    if (!data?.inativarTorneio) {
      throw new Error('Não foi possível inativar o torneio.');
    }
    return data.inativarTorneio;
  } catch (error: any) {
    let message = 'Erro ao inativar torneio.';
    if (error?.graphQLErrors?.[0]?.message) {
      message = error.graphQLErrors[0].message;
    } else if (error?.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
}

export interface AtivarTorneioVars {
  id: string;
}

export interface AtivarTorneioData {
  ativarTorneio: Torneio & { status?: string | null };
}

export async function ativarTorneio(id: string): Promise<Torneio & { status?: string | null }> {
  try {
    const { data } = await apolloClient.mutate<AtivarTorneioData, AtivarTorneioVars>({
      mutation: ATIVAR_TORNEIO,
      variables: { id },
    });
    if (!data?.ativarTorneio) {
      throw new Error('Não foi possível ativar o torneio.');
    }
    return data.ativarTorneio;
  } catch (error: any) {
    let message = 'Erro ao ativar torneio.';
    if (error?.graphQLErrors?.[0]?.message) {
      message = error.graphQLErrors[0].message;
    } else if (error?.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
}

export interface TorneiosFiltradosVars {
  nome?: string | null;
  cidade?: string | null;
  ligaId?: string | null;
  data?: string | null;
  status?: StatusTorneio | null;
  ativo?: boolean | null;
  page: number;
  size: number;
  sortBy?: string | null;
  direction?: string | null;
  ocultarPassados?: boolean | null;
  apenasVigentes?: boolean | null;
}

export interface TorneiosFiltradosData {
  torneiosFiltrados: {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
    items: Torneio[];
  };
}

export const CRIAR_TORNEIO = gql`
  mutation CriarTorneio($input: TorneioInput!) {
    criarTorneio(input: $input) {
      id
      nome
      ativo
      dataFim
      dataInicio
      dataInscricaoFim
      dataInscricaoInicio
      descricao
      subtitulo
      username
      criadoEm
      atualizadoEm
    }
  }
`;

export interface TorneioInput {
  nome: string;
  dataInicio: string;
  dataFim: string;
  ativo?: boolean | null;
  dataInscricaoFim?: string | null;
  dataInscricaoInicio?: string | null;
  descricao?: string | null;
  subtitulo?: string | null;
  username?: string | null;
  regulamento?: string | null;
  ligaId?: string | null;
  status?: StatusTorneio | null;
  filaEspera?: boolean | null;
  autoFilaEspera?: boolean | null;
  exibirFilaEspera?: boolean | null;
  camisetas?: boolean | null;
  sapatos?: boolean | null;
  atletaTrocaParceiro?: boolean | null;
  exibirInscritos?: boolean | null;
  autoCancNaoPagas?: boolean | null;
  validarElegibilidade?: boolean | null;
}

export interface TorneioResponse {
  id: string;
  nome?: string;
  dataInicio?: string;
  dataFim?: string;
}

interface TorneioData {
  criarTorneio: TorneioResponse;
}

interface TorneioVars {
  input: TorneioInput;
}

export async function criarTorneio(
  input: TorneioInput,
): Promise<TorneioResponse> {
  const processedInput = {
    ...input,
    dataInscricaoInicio: input.dataInscricaoInicio ? toLocalDateTimeString(input.dataInscricaoInicio) : null,
    dataInscricaoFim: input.dataInscricaoFim ? toLocalDateTimeString(input.dataInscricaoFim) : null,
  };
  const { data } = await apolloClient.mutate<
    TorneioData,
    TorneioVars
  >({
    mutation: CRIAR_TORNEIO,
    variables: { input: processedInput },
  });
  return data?.criarTorneio as TorneioResponse;
}

export const DELETAR_TORNEIO = gql`
  mutation DeletarTorneio($id: ID!) {
    deletarTorneio(id: $id)
  }
`;

export interface DeletarTorneioVars {
  id: string;
}

export interface DeletarTorneioData {
  deletarTorneio: boolean;
}

export async function deletarTorneio(id: string): Promise<boolean> {
  try {
    const { data } = await apolloClient.mutate<DeletarTorneioData, DeletarTorneioVars>({
      mutation: DELETAR_TORNEIO,
      variables: { id },
    });
    return data?.deletarTorneio ?? false;
  } catch (error: any) {
    let message = 'Erro ao deletar torneio.';
    if (error?.graphQLErrors?.[0]?.message) {
      message = error.graphQLErrors[0].message;
    } else if (error?.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
}

export const DUPLICAR_TORNEIO = gql`
  mutation DuplicarTorneio($id: ID!, $input: DuplicarTorneioInput!) {
    duplicarTorneio(id: $id, input: $input) {
      id
      nome
      username
      descricao
      subtitulo
      ativo
      status
      dataInicio
      dataFim
      dataInscricaoInicio
      dataInscricaoFim
      liga {
        id
        nome
      }
      categoriasTorneio {
        id
        nome
        dataInicio
        dataFim
        dataLimPag
      }
      organizadores {
        id
        nome
        nomeFuncao
      }
      patrocinadores {
        id
        nome
        descricao
        link
      }
      descontoCategorias {
        id
        tipo
        valor
        ordem
      }
    }
  }
`;

export interface DuplicarTorneioInput {
  nome: string;
  username: string;
  dataInicio: string;
  dataFim: string;
  dataInscricaoInicio: string;
  dataInscricaoFim: string;
}

export interface DuplicarTorneioVars {
  id: string;
  input: DuplicarTorneioInput;
}

export interface DuplicarTorneioData {
  duplicarTorneio: Torneio;
}

export async function duplicarTorneio(
  id: string,
  input: DuplicarTorneioInput,
): Promise<Torneio> {
  try {
    const { data } = await apolloClient.mutate<DuplicarTorneioData, DuplicarTorneioVars>({
      mutation: DUPLICAR_TORNEIO,
      variables: { id, input },
    });
    if (!data?.duplicarTorneio) {
      throw new Error('Não foi possível duplicar o torneio.');
    }
    return data.duplicarTorneio;
  } catch (error: any) {
    let message = 'Erro ao duplicar torneio.';
    if (error?.graphQLErrors?.[0]?.message) {
      message = error.graphQLErrors[0].message;
    } else if (error?.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
}

export const ATUALIZAR_TORNEIO = gql`
  mutation AtualizarTorneio(
    $id: ID!
    $nome: String!
    $username: String!
    $subtitulo: String
    $descricao: String
    $bannerUrl: String
    $bannerVUrl: String
    $avatarUrl: String
    $dataInicio: String!
    $dataFim: String
    $regulamento: String
    $dataInscricaoInicio: String
    $dataInscricaoFim: String
    $localId: ID
    $status: StatusTorneio
    $filaEspera: Boolean
    $autoFilaEspera: Boolean
    $exibirFilaEspera: Boolean
    $camisetas: Boolean
    $sapatos: Boolean
    $atletaTrocaParceiro: Boolean
    $exibirInscritos: Boolean
    $autoCancNaoPagas: Boolean
    $validarElegibilidade: Boolean
  ) {
    atualizarTorneio(
      id: $id,
      input: {
        nome: $nome
        username: $username
        subtitulo: $subtitulo
        descricao: $descricao
        bannerUrl: $bannerUrl
        bannerVUrl: $bannerVUrl
        avatarUrl: $avatarUrl
        dataInicio: $dataInicio
        dataFim: $dataFim
        regulamento: $regulamento
        dataInscricaoInicio: $dataInscricaoInicio
        dataInscricaoFim: $dataInscricaoFim
        localId: $localId
        status: $status
        filaEspera: $filaEspera
        autoFilaEspera: $autoFilaEspera
        exibirFilaEspera: $exibirFilaEspera
        camisetas: $camisetas
        sapatos: $sapatos
        atletaTrocaParceiro: $atletaTrocaParceiro
        exibirInscritos: $exibirInscritos
        autoCancNaoPagas: $autoCancNaoPagas
        validarElegibilidade: $validarElegibilidade
      }
    ) {
      id
      nome
      descricao
      subtitulo
      username
      bannerUrl
      bannerVUrl
      avatarUrl
      dataInicio
      dataFim
      regulamento
      dataInscricaoInicio
      dataInscricaoFim
      status
      filaEspera
      autoFilaEspera
      exibirFilaEspera
      camisetas
      sapatos
      atletaTrocaParceiro
      exibirInscritos
      autoCancNaoPagas
      validarElegibilidade
    }
  }
`;

export interface AtualizarTorneioVars {
  id: string;
  nome: string;
  username: string;
  subtitulo?: string | null;
  descricao?: string | null;
  bannerUrl?: string | null;
  bannerVUrl?: string | null;
  avatarUrl?: string | null;
  dataInicio: string;
  dataFim?: string | null;
  regulamento?: string | null;
  dataInscricaoInicio?: string | null;
  dataInscricaoFim?: string | null;
  localId?: string | null;
  status?: StatusTorneio | null;
  filaEspera?: boolean | null;
  autoFilaEspera?: boolean | null;
  exibirFilaEspera?: boolean | null;
  camisetas?: boolean | null;
  sapatos?: boolean | null;
  atletaTrocaParceiro?: boolean | null;
  exibirInscritos?: boolean | null;
  autoCancNaoPagas?: boolean | null;
  validarElegibilidade?: boolean | null;
}

interface AtualizarTorneioMutationData {
  atualizarTorneio: TorneioProfile;
}

type AtualizarTorneioPayload = Partial<AtualizarTorneioVars> & { id: string };

export const atualizarAvatarTorneio = async (id: string, arquivo: File) => {
  const formData = new FormData();
  formData.append('file', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<string>(
      `${restUrl}torneios/${id}/avatar`,
      formData,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};
export const uploadBannerTorneio = async (id: string, arquivo: File): Promise<{ bannerUrl: string }> => {
  const formData = new FormData();
  formData.append('file', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<string>(
      `${restUrl}torneios/${id}/banner`,
      formData,
      { headers }
    );
    return { bannerUrl: response.data ?? '' };
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};

export const uploadBannerVTorneio = async (id: string, arquivo: File): Promise<{ bannerVUrl: string }> => {
  const formData = new FormData();
  formData.append('file', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<string>(
      `${restUrl}torneios/${id}/banner-v`,
      formData,
      { headers }
    );
    return { bannerVUrl: response.data ?? '' };
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};

const handleAtualizarTorneioError = (error: any): never => {
  if ((error?.networkError as any)?.statusCode === 401) {
    dispatchLogout();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw error;
  }

  let message = 'Erro inesperado. Tente novamente.';
  if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
    message = error.graphQLErrors[0].message || message;
  } else if (error?.message) {
    message = error.message;
  }

  dispatchGlobalError(message);
  throw error;
};

export const atualizarTorneio = async (payload: AtualizarTorneioPayload): Promise<TorneioProfile> => {
  let baseTorneio: Torneio | null = null;

  try {
    const { data } = await apolloClient.query<TorneioPorIdData, TorneioPorIdVars>({
      query: TORNEIO_POR_ID,
      variables: { id: payload.id },
      fetchPolicy: 'network-only',
    });

    baseTorneio = data?.torneioPorId ?? null;
  } catch (error: any) {
    return handleAtualizarTorneioError(error);
  }

  if (!baseTorneio) {
    const error = new Error('Torneio nao encontrado.');
    dispatchGlobalError(error.message);
    throw error;
  }

  const variables: AtualizarTorneioVars = {
    id: payload.id,
    nome: payload.nome ?? baseTorneio.nome ?? '',
    username: payload.username ?? baseTorneio.username ?? '',
    subtitulo: payload.subtitulo !== undefined ? payload.subtitulo : baseTorneio.subtitulo ?? null,
    descricao: payload.descricao !== undefined ? payload.descricao : baseTorneio.descricao ?? null,
    bannerUrl: payload.bannerUrl !== undefined ? payload.bannerUrl : baseTorneio.bannerUrl ?? null,
    bannerVUrl: payload.bannerVUrl !== undefined ? payload.bannerVUrl : baseTorneio.bannerVUrl ?? null,
    avatarUrl: payload.avatarUrl !== undefined ? payload.avatarUrl : baseTorneio.avatarUrl ?? null,
    dataInicio: payload.dataInicio ?? baseTorneio.dataInicio ?? '',
    dataFim: payload.dataFim !== undefined ? payload.dataFim : baseTorneio.dataFim ?? null,
    regulamento: payload.regulamento !== undefined ? payload.regulamento : baseTorneio.regulamento ?? null,
    dataInscricaoInicio: payload.dataInscricaoInicio !== undefined ? toLocalDateTimeString(payload.dataInscricaoInicio) : baseTorneio.dataInscricaoInicio ?? null,
    dataInscricaoFim: payload.dataInscricaoFim !== undefined ? toLocalDateTimeString(payload.dataInscricaoFim) : baseTorneio.dataInscricaoFim ?? null,
    localId: payload.localId !== undefined ? payload.localId : baseTorneio.local?.id ?? null,
    status: payload.status !== undefined ? payload.status : baseTorneio.status ?? null,
    filaEspera: payload.filaEspera !== undefined ? payload.filaEspera : baseTorneio.filaEspera ?? null,
    autoFilaEspera: payload.autoFilaEspera !== undefined ? payload.autoFilaEspera : baseTorneio.autoFilaEspera ?? null,
    exibirFilaEspera: payload.exibirFilaEspera !== undefined ? payload.exibirFilaEspera : baseTorneio.exibirFilaEspera ?? null,
    camisetas: payload.camisetas !== undefined ? payload.camisetas : baseTorneio.camisetas ?? null,
    sapatos: payload.sapatos !== undefined ? payload.sapatos : baseTorneio.sapatos ?? null,
    atletaTrocaParceiro: payload.atletaTrocaParceiro !== undefined ? payload.atletaTrocaParceiro : baseTorneio.atletaTrocaParceiro ?? null,
    exibirInscritos: payload.exibirInscritos !== undefined ? payload.exibirInscritos : baseTorneio.exibirInscritos ?? null,
    autoCancNaoPagas: payload.autoCancNaoPagas !== undefined ? payload.autoCancNaoPagas : baseTorneio.autoCancNaoPagas ?? null,
    validarElegibilidade: payload.validarElegibilidade !== undefined ? payload.validarElegibilidade : baseTorneio.validarElegibilidade ?? null,
  };

  try {
    const { data } = await apolloClient.mutate<AtualizarTorneioMutationData, AtualizarTorneioVars>({
      mutation: ATUALIZAR_TORNEIO,
      variables,
    });

    if (!data?.atualizarTorneio) {
      const error = new Error('Resposta invalida ao atualizar torneio.');
      dispatchGlobalError(error.message);
      throw error;
    }

    return data.atualizarTorneio;
  } catch (error: any) {
    return handleAtualizarTorneioError(error);
  }
};

// --------------------------------------------------------------------------------
// Query paginada: torneios em que o atleta está inscrito
// --------------------------------------------------------------------------------

export const TORNEIOS_POR_ATLETA = gql`
  query TorneiosPorAtletaId($atletaId: ID!, $page: Int!, $size: Int!) {
    torneiosPorAtletaId(atletaId: $atletaId, page: $page, size: $size) {
      page
      size
      totalItems
      totalPages
      items {
        id
        nome
        avatarUrl
        dataInicio
        dataFim
        local {
          id
          nome
          urlLocalizacao
          endereco {
            id
            cidade {
              id
              nome
              uf
            }
          }
        }
      }
    }
  }
`;

export interface TorneioResumoAtleta {
  id: string;
  nome: string;
  avatarUrl?: string | null;
  dataInicio?: string | null;
  dataFim?: string | null;
  local?: {
    id: string;
    nome: string;
    urlLocalizacao?: string | null;
    endereco?: {
      id: string;
      cidade: {
        id: string;
        nome: string;
        uf: string;
      };
    } | null;
  } | null;
}

export interface TorneiosPorAtletaData {
  torneiosPorAtletaId: {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
    items: TorneioResumoAtleta[];
  };
}

export interface TorneiosPorAtletaVars {
  atletaId: string;
  page: number;
  size: number;
}

export const COUNT_INSCRITOS_TORNEIO_QUERY = gql`
  query CountInscritosTorneio($torneioId: ID!) {
    countInscritosTorneio(torneioId: $torneioId)
  }
`;

interface CountInscritosTorneioData {
  countInscritosTorneio: number | string | null;
}

interface CountInscritosTorneioVars {
  torneioId: string;
}

/**
 * Conta o número de inscritos em um torneio.
 *
 * @param torneioId - ID do torneio a consultar.
 * @param opts - Opções adicionais da query (ex.: fetchPolicy).
 * @returns Quantidade de inscritos.
 *
 * @example
 * const total = await countInscritosTorneio('123');
 */
export async function countInscritosTorneio(
  torneioId: string,
  opts?: { fetchPolicy?: string },
): Promise<number> {
  if (!torneioId || torneioId.trim() === '') {
    throw new Error('torneioId deve ser uma string não vazia.');
  }

  try {
    const { data } = await apolloClient.query<
      CountInscritosTorneioData,
      CountInscritosTorneioVars
    >({
      query: COUNT_INSCRITOS_TORNEIO_QUERY,
      variables: { torneioId },
      fetchPolicy: (opts?.fetchPolicy as any) ?? 'cache-first',
    });

    const count = data?.countInscritosTorneio;
    if (typeof count === 'number') {
      return count;
    }
    if (typeof count === 'string') {
      const parsed = Number(count);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  } catch (error) {
    throw new Error('Erro ao contar inscritos do torneio.');
  }
}

// Tentativa de query direta (se disponível no backend)
// Usa o mesmo campo do backend "inscricoesTorneioPorAtletaIdTorneioId", porém
// passando apenas torneioId (sem atletaId) para listar todas as inscrições do torneio.
export const INSCRICOES_POR_TORNEIO = gql`
  query InscricoesPorTorneio($torneioId: ID!) {
    inscricoesTorneioPorAtletaIdTorneioId(atletaId: "", torneioId: $torneioId) {
      id
      status
      dataInscricao
      dataCancelamento
      motivoCancelamento
      observacao
      isento
      equipe {
        id
        nome
        cidade
        atletas {
          id
          usuario {
            id
            nome
            username
            imagemUrl
          }
        }
      }
      categoriaTorneio {
        id
        nome
        aplicarDescontoTorneio
      }
      pagamentoInscricao {
        id
        status
        valor
        pedidosReembolso { id status }
      }
    }
  }
`;

type InscricaoItemGraphQL = {
  id: string;
  status?: string | null;
  dataInscricao?: string | null;
  dataCancelamento?: string | null;
  motivoCancelamento?: string | null;
  observacao?: string | null;
  isento: boolean;
  equipe?: {
    id: string;
    nome: string;
    cidade?: string | null;
    atletas: {
      id: string;
      usuario: { id: string; nome: string; imagemUrl?: string | null; username?: string };
    }[];
  } | null;
  categoriaTorneio?: { id: string; nome: string; aplicarDescontoTorneio?: boolean | null } | null;
  pagamentoInscricao?: {
    id: string;
    status: string;
    valor?: number | null;
    pedidosReembolso?: { id: string; status: string }[] | null;
  } | null;
};

export interface InscricoesPorTorneioData {
  inscricoesTorneioPorAtletaIdTorneioId: InscricaoItemGraphQL[];
}

export interface InscricoesPorTorneioVars {
  torneioId: string;
}

// Query leve para contagem de vagas ocupadas por categoria (PENDENTE + CONFIRMADA)
export const CONTAGEM_VAGAS_POR_CATEGORIA = gql`
  query ContagemVagasPorCategoria($torneioId: ID!) {
    contagemVagasPorCategoria(torneioId: $torneioId) {
      categoriaId
      ocupadas
    }
  }
`;

export interface VagasPorCategoria {
  categoriaId: string;
  ocupadas: number;
}

export interface ContagemVagasData {
  contagemVagasPorCategoria: VagasPorCategoria[];
}

export interface ContagemVagasVars {
  torneioId: string;
}

export async function listarInscritosPorTorneioAgrupadoPorCategoria(
  torneioId: string,
): Promise<Record<string, InscritoAtleta[]>> {
  if (!torneioId || torneioId.trim() === '') {
    throw new Error('torneioId deve ser uma string não vazia.');
  }

  // 1) Tenta query direta ao backend via inscricoesTorneioPorAtletaIdTorneioId(torneioId)
  const grouped: Record<string, InscritoAtleta[]> = {};
  try {
    const { data } = await apolloClient.query<InscricoesPorTorneioData, InscricoesPorTorneioVars>({
      query: INSCRICOES_POR_TORNEIO,
      variables: { torneioId },
      fetchPolicy: 'no-cache',
    });
    const list = data?.inscricoesTorneioPorAtletaIdTorneioId || [];
    let sawCategory = false;
    list.forEach((item) => {
      const catNome = item.categoriaTorneio?.nome || 'Sem categoria';
      if (item.categoriaTorneio?.nome) sawCategory = true;
      if (!grouped[catNome]) grouped[catNome] = [];
      const status = item.status ?? null;
      const inscricaoId = item.id;
      const atleta = (item as any).atleta as
        | {
            id: string;
            usuario?: { id: string; nome: string; username?: string; imagemUrl?: string | null } | null;
            cidade?: { id: string; nome: string; uf: string } | null;
          }
        | undefined;

      if (atleta) {
        grouped[catNome].push({
          id: atleta.id,
          nome: atleta.usuario?.nome || '',
          username: atleta.usuario?.username || '',
          imagemUrl: atleta.usuario?.imagemUrl ?? null,
          cidade: atleta.cidade?.nome ? `${atleta.cidade.nome}${atleta.cidade.uf ? `/${atleta.cidade.uf}` : ''}` : null,
          status,
          equipeId: null,
          inscricaoId,
        });
      }
    });

    // Se retornou dados e pelo menos uma inscrição tem categoria, retorna direto.
    const hasAny = Object.values(grouped).some((arr) => (arr?.length || 0) > 0);
    if (hasAny && sawCategory) return grouped;
  } catch (_e) {
    // ignora e tenta fallback
  }

  // 2) Fallback: compõe via categorias do torneio e equipes por categoria
  try {
    const { data } = await apolloClient.query<TorneioPorIdData, TorneioPorIdVars>({
      query: TORNEIO_POR_ID,
      variables: { id: torneioId },
      fetchPolicy: 'no-cache',
    });
    const categorias = data?.torneioPorId?.categoriasTorneio || [];
    for (const cat of categorias) {
      const catNome = cat?.nome || 'Sem categoria';
      if (!grouped[catNome]) grouped[catNome] = [];
      const equipes = await buscarEquipesPorCategoriaTorneio(cat.id);
      for (const equipe of equipes) {
        for (const atleta of equipe.atletas) {
          grouped[catNome].push({
            id: atleta.id,
            nome: atleta.usuario?.nome || '',
            username: atleta.usuario?.username || '',
            imagemUrl: atleta.usuario?.imagemUrl ?? null,
            cidade: atleta.cidade?.nome ? `${atleta.cidade.nome}${atleta.cidade.uf ? `/${atleta.cidade.uf}` : ''}` : null,
            status: null,
            equipeId: equipe.id,
            inscricaoId: null,
          });
        }
      }
    }
    return grouped;
  } catch (e) {
    throw new Error('Erro ao listar inscritos do torneio.');
  }
}

export const TORNEIO_ELEGIBILIDADE_POR_ID = gql`
  query TorneioElegibilidadePorId($id: ID!) {
    torneioPorId(id: $id) {
      id
      validarElegibilidade
    }
  }
`;

interface TorneioElegibilidadeData {
  torneioPorId: { id: string; validarElegibilidade?: boolean | null } | null;
}

/**
 * Resolve a flag `validarElegibilidade` de um torneio pelo id.
 * Usada pelas telas de categoria (criação e edição) para decidir se os
 * campos de gênero/idade devem aparecer — inclusive no fluxo de criação,
 * onde ainda não há categoria carregada para derivar o torneio.
 */
export async function buscarValidarElegibilidadeTorneio(
  id: string,
): Promise<boolean> {
  if (!id) return false;
  try {
    const { data } = await apolloClient.query<TorneioElegibilidadeData>({
      query: TORNEIO_ELEGIBILIDADE_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    return data?.torneioPorId?.validarElegibilidade ?? false;
  } catch (e) {
    // Degradação não-crítica: a visibilidade dos campos opcionais de
    // elegibilidade não deve derrubar a tela de categoria. O erro é
    // registrado para diagnóstico (não é supressão silenciosa).
    console.error('Erro ao resolver validarElegibilidade do torneio:', e);
    return false;
  }
}
